import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import Box from "@mui/material/Box";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { GetPersonalFavoriteListResponse, GetPersonalFavoriteListsResponse } from "api/generatedApi";
import { sortCompareString } from "utils/compares";
import { useOrder } from "shared/table/use-order";
import { TableHeaderConfig } from "shared/table/table-header-type";
import { ListTableHeader } from "../list-table-header";
import { useDeleteFavoriteList } from "../../hooks/use-confirm-delete-list-dialog";
import { useCreateList } from "../../hooks/use-create-list-dialog";
import { Fab } from "@mui/material";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { MobileListRow } from "./mobile-list-row";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useShareList } from "../../hooks/use-share-list-dialog";
import { useEditList } from "../../hooks/use-edit-list-dialog";
import { useFeatures } from "utils/hooks/use-features";

interface Props {
  favoriteLists: GetPersonalFavoriteListsResponse | undefined;
  onItemClick: (id: string) => void;
}

export const MobileListList = (props: Props) => {
  type FavoritesSortableId = "Text" | "Unit";

  const { isFeatureEnabled } = useFeatures();

  const { favoriteLists } = props;
  const { t } = useTranslation();
  const { direction, orderBy, getLabelProps } = useOrder<FavoritesSortableId>("Text");

  const [choosenList, setChoosenList] = useState<GetPersonalFavoriteListResponse | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const openEditListDialog = useEditList({ favoriteListId: selectedId ?? "", listName: choosenList?.name ?? "" });
  const openShareListDialog = useShareList({ favoriteListId: selectedId ?? "" });
  const openDeleteFavoritesDialog = useDeleteFavoriteList({
    listId: selectedId ?? "",
    isOwner: choosenList?.isOwnedByCurrentUser ?? false,
  });
  const openCreateList = useCreateList();

  // Single item selection logic
  const onCheck = useCallback((id: string | undefined) => {
    if (id === undefined) return;

    setChoosenList(favoriteLists?.lists?.find((list) => list.id === id));
    // Toggle selection - if same ID is clicked, deselect it
    setSelectedId((prevId) => (prevId === id ? undefined : id));
  }, []);

  const isFavoriteChecked = (id: string | undefined): boolean => {
    return selectedId === id;
  };

  const sortedData = useMemo(() => {
    let sortedList = favoriteLists?.lists;

    switch (orderBy) {
      case "Text":
        if (favoriteLists?.lists) {
          sortedList = [...favoriteLists.lists].sort((a, b) => sortCompareString(direction, a?.name, b?.name));
        }
        break;
    }

    return sortedList;
  }, [favoriteLists, orderBy, direction]);

  const buttonDisabled = (): boolean => {
    if (favoriteLists?.lists?.findIndex((list) => list.id === selectedId && !list.isOwnedByCurrentUser) !== -1) {
      return false;
    } else if (selectedId) {
      return false;
    }

    return true;
  };

  const headerConfig: TableHeaderConfig<FavoritesSortableId>[] = [
    {
      id: "Text",
      title: t("account.favorites.tableName"),
      sortable: true,
      testid: "account-favorites-header-name",
      alignment: "left",
    },
  ];

  // Dummy function since check-all is disabled, but is set up to work if needed in the future
  const onCheckAll = () => {
    // Do nothing - check all is disabled
  };

  return (
    <Box sx={{ flex: 1, overflowY: "hidden", display: "flex", flexDirection: "column", width: "100%", height: "100%", p: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
        <IconButton data-testid="account-favorites-delete-rows" disabled={buttonDisabled()} onClick={openDeleteFavoritesDialog}>
          <DeleteOutlinedIcon />
        </IconButton>
        {isFeatureEnabled("shareFavorites") && (
          <IconButton data-testid="account-favorites-share-rows" disabled={buttonDisabled()} onClick={openShareListDialog}>
            <AddCircleOutlineIcon />
          </IconButton>
        )}
        <IconButton data-testid="account-favorites-edit-rows" disabled={buttonDisabled()} onClick={openEditListDialog}>
          <EditIcon />
        </IconButton>
      </Box>
      <TableContainer sx={{ height: "calc(100vh - 240px)", overflowY: "auto", pb: "90px" }} component={Paper}>
        <Table stickyHeader>
          <ListTableHeader method="checkbox" headerConfig={headerConfig} getLabelProps={getLabelProps} checked={false} checkBoxDisabled={true} onCheckAllProps={onCheckAll} hideCheckAll={true} />
          <TableBody>
            {sortedData &&
              sortedData.map((favorite) => {
                return <MobileListRow key={favorite.id} row={favorite} setChoosenList={props.onItemClick} onCheck={onCheck} checked={isFavoriteChecked(favorite.id)} />;
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Fab
        sx={{
          position: "absolute",
          bottom: "70px",
          right: "20px",
          backgroundColor: "primary.dark",
        }}
        onClick={openCreateList}
      >
        <TimerOutlinedIcon fontSize="large" sx={{ color: "grey.100", pb: "2px" }} />
      </Fab>
    </Box>
  );
};
